<script setup lang="ts">
/**
 * #234: the plans parked on a CEO decision, in one place. Every sidebar badge routes to a list page
 * showing everything the count counts — Gates, Blockers, Self-Improvement all do — so the Plans badge
 * gets the same: `GET /plans?status=awaiting_ceo`, one row per plan, each linking through to its own
 * permanent page (`/plans/:id`, #227) where the decision is actually made. Deciding happens on the
 * plan (or its run), never from a bare click here, so this page has no action buttons.
 */
import { ZhPageTitle, ZhTooltip } from '@zerohuman/ui'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { api, formatAgo, PLAN_STATUS_AWAITING_CEO, type PlanRow, waitedMs } from '@/api'
import DataTable, { type DataTableColumn } from '@/components/DataTable.vue'
import FirstRunHint from '@/components/FirstRunHint.vue'
import { useClientTable } from '@/composables/useClientTable'
import { useListState } from '@/composables/useListState'

/** A plan row as the table shows it: what it proposes, what was asked for, and how long it has waited. */
type PlanTableRow = PlanRow & {
    /** The plan's own words, falling back to the intent and then its id so a row is never blank. */
    summaryText: string
    intent: string
    waited: string
}

/** The sidebar badge counting these plans polls on 5s; the list it links to keeps the same beat. */
const REFRESH_MS = 5000

const plans = ref<PlanRow[]>([])
const error = ref<string | null>(null)
const loading = ref(true)
/** Ticks with each refresh so the wait time stays live. */
const now = ref(Date.now())
let timer: ReturnType<typeof setInterval> | null = null

const rows = computed<PlanTableRow[]>(() =>
    plans.value.map(plan => ({
        ...plan,
        summaryText: plan.summary?.trim() || plan.ceoIntent?.trim() || plan.id,
        intent: plan.ceoIntent?.trim() ?? '',
        waited: formatAgo(waitedMs(plan, now.value)),
    })),
)

const table = useClientTable({
    data: rows,
    searchFields: ['summaryText', 'intent', 'kind'],
    defaultSort: 'createdAt',
    defaultOrder: 'ASC',
})
/** #633: the table's search, sort and filters come back on the next visit. */
const list = useListState('plans', table.listFields)

/**
 * Nothing is waiting on a decision: the table (with its search and filters) says less than a sentence
 * does, so the page says it plainly. Keyed off what the API returned, never the filtered rows — a
 * search that narrows real plans to zero must keep the table, or there is no way to clear the search.
 */
const noPlans = computed(() => !loading.value && plans.value.length === 0)

const columns: DataTableColumn[] = [
    { key: 'summaryText', label: 'Plan', sort: 'summaryText', filterable: false },
    { key: 'intent', label: 'Asked for', filterable: false },
    { key: 'kind', label: 'Kind', sort: 'kind', type: 'text' },
    { key: 'waited', label: 'Waiting', sort: 'createdAt', filterable: false },
]

onMounted(() => {
    void load()
    timer = setInterval(() => {
        now.value = Date.now()
        void load()
    }, REFRESH_MS)
})

onUnmounted(() => {
    if (timer) {
        clearInterval(timer)
    }
})

async function load(): Promise<void> {
    try {
        // Scoped to the decision this page is about: an approved or rejected plan is history, and lives
        // on its own page, not in a queue of things waiting on the CEO.
        plans.value = (await api.plans({ status: PLAN_STATUS_AWAITING_CEO })) ?? []
        error.value = null
    } catch {
        // Last known plans stay on screen, the same way the sidebar keeps its last known counts.
        error.value = 'Could not load plans.'
    } finally {
        loading.value = false
    }
}
</script>

<template>
	<section class="w-full space-y-4 p-8">
		<ZhPageTitle>Plans</ZhPageTitle>
		<p class="text-muted-foreground text-sm">
			Plans waiting on your decision: what each one proposes, what was asked for, and how long it has
			waited. Open a plan to read it in full and decide it.
		</p>
		<p v-if="error" class="text-destructive text-sm">{{ error }}</p>
		<FirstRunHint
			v-if="noPlans"
			lead="No plan is waiting on your decision. Plans your tasks propose appear here for you to decide."
			:action="{ label: 'Go to Tasks', to: '/tasks' }"
		/>
		<DataTable
			v-else
			storage-key="plans"
			:columns="columns"
			:loading="loading"
			:stacked-on-mobile="true"
			:list-state="list"
			v-bind="table.tableProps.value"
		>
			<template #cell-summaryText="{ row, value }">
				<!-- The decision is made on the plan's own page, so the row links straight at it. -->
				<RouterLink class="font-medium underline" :to="`/plans/${row.id}`">
					{{ value }}
				</RouterLink>
			</template>
			<template #cell-intent="{ value }">
				<ZhTooltip v-if="value" :text="String(value)">
					<span class="line-clamp-2 block max-w-md whitespace-pre-line">{{ value }}</span>
				</ZhTooltip>
				<span v-else class="text-muted-foreground">—</span>
			</template>
			<template #cell-waited="{ row, value }">
				<ZhTooltip :text="new Date(String(row.createdAt)).toLocaleString()">
					<span class="tabular-nums">{{ value }}</span>
				</ZhTooltip>
			</template>
		</DataTable>
	</section>
</template>
